const KeenSlider = window.KeenSlider
const { setInitialised, useArrows } = window.BAO.Carousel.plugins

const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return (carousel) => {

    const updateDotStates = () => {
      const { maxIdx } = carousel.track.details

      dots.forEach((dot, index) => {
        if (index > maxIdx) {
          dot.setAttribute('aria-hidden', true)
        } else {
          dot.removeAttribute('aria-hidden')
        }

        const isCurrent = carousel.track.details.rel === index

        dot.setAttribute('aria-current', String(isCurrent))
      })
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        carousel.moveToIdx(index)
      })
    })

    carousel.on('created', updateDotStates)
    carousel.on('slideChanged', updateDotStates)
  }
}

class ProductCarousel extends HTMLElement {
  static get observedAttributes() {
    return ['aria-hidden'];
  }

  constructor() {
    super()
    this.onThemeEditorEvent = this.onThemeEditorEvent.bind(this)
  }

  connectedCallback() {
    this.slider = this.querySelector('[data-product-carousel-el="slider"]')
    this.prev = this.querySelector('[data-product-carousel-el="prev"]')
    this.next = this.querySelector('[data-product-carousel-el="next"]')
    this.slides = '[data-product-carousel-el="slide"]'
    this.dots = this.querySelectorAll('[data-product-carousel-el="dot"]')

    this.setupCarousel()
    this.addThemeEditorListeners()
  }

  disconnectedCallback() {
    this.removeThemeEditorListeners()
    this.destroyCarousel()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'aria-hidden' && oldValue !== newValue) {
      this.setupCarousel()
    }
  }

  setupCarousel() {
    this.destroyCarousel()

    if (!this.slider || this.getAttribute('aria-hidden') === 'true') return null

    this.carousel = new KeenSlider(
      this.slider,
      {
        selector: '[data-product-carousel-el="slide"]',
        defaultAnimation: {
          duration: 1500,
        },
        loop: false,
        mode: 'snap',
        slides: {
          perView: 2.1,
          spacing: 10,
        },
        breakpoints: {
          '(min-width: 990px)': {
            slides: {
              perView: 3.2,
              spacing: 12,
            },
          },
        },
      },
      [
        setInitialised(),
        useArrows({
          next: this.next,
          previous: this.prev,
        }),
        useDots(this.dots)
      ],
    )

    return this.carousel
  }

  destroyCarousel() {
    this.carousel?.destroy()
    this.carousel = null
  }

  addThemeEditorListeners() {
    if (!window.Shopify?.designMode || this.themeEditorListenersAttached) return

    this.themeEditorListenersAttached = true
    document.addEventListener('shopify:section:load', this.onThemeEditorEvent)
    document.addEventListener('shopify:section:select', this.onThemeEditorEvent)
    document.addEventListener('shopify:section:reorder', this.onThemeEditorEvent)
    document.addEventListener('shopify:inspector:activate', this.onThemeEditorEvent)
    document.addEventListener('shopify:inspector:deactivate', this.onThemeEditorEvent)
  }

  removeThemeEditorListeners() {
    if (!this.themeEditorListenersAttached) return

    this.themeEditorListenersAttached = false
    document.removeEventListener('shopify:section:load', this.onThemeEditorEvent)
    document.removeEventListener('shopify:section:select', this.onThemeEditorEvent)
    document.removeEventListener('shopify:section:reorder', this.onThemeEditorEvent)
    document.removeEventListener('shopify:inspector:activate', this.onThemeEditorEvent)
    document.removeEventListener('shopify:inspector:deactivate', this.onThemeEditorEvent)
  }

  onThemeEditorEvent() {
    window.requestAnimationFrame(() => {
      if (this.getAttribute('aria-hidden') === 'true') {
        this.destroyCarousel()
        return
      }

      if (!this.carousel) {
        this.setupCarousel()
        return
      }

      this.carousel.update()
    })
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel)
}
